import { AppShell } from "@/components/app-shell";
import { PresenceRail } from "@/components/presence-rail";
import { ReviewStageSkeleton } from "@/components/mission-skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type AgentFinding,
  type Artifact,
  type CircuitDiff,
  type CircuitDiffFile,
  type FindingsEnvelope,
  type HardwareRevision,
  type Job,
  type Review,
  type ReviewComment,
  artifactUrl,
  findImportSvg,
  getCircuitDiff,
  getFindings,
  getHardware,
  getReview,
  jobType,
  parseJsonField,
  pollJob,
  postReviewComment,
  revisionHasPcb,
} from "@/lib/api";
import {
  COLLAB_IA,
  canDecideReview,
  changeOidRange,
  checkConclusionLabel,
  decideReview,
  findingPinLabel,
  getChange,
  jobsCheckRuns,
  reviewApproveUi,
  reviewStatusLabel,
  type ChangeListItem,
  type ReviewVerdict,
} from "@/lib/collaboration";
import { AGENT_FINDINGS_NOT_A_GATE, jobTypeLabel } from "@/lib/job-kind-labels";
import { findingsWaitingCopy } from "@/lib/job-drain";
import { useProjectScope } from "@/lib/project-scope";
import { useKeyedLoad } from "@/lib/scope-loading";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";

/** Beautify V1 schematic paper. Think rail stays in AppShell (48 / open). */
export const REVIEW_SCHEMATIC_INSET = "#FAFAF8";
export const REVIEW_STAGE_COLS = "lg:grid-cols-[220px_minmax(0,1fr)_300px]";

function pathBase(path: string): string {
  const i = path.replaceAll("\\", "/").lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}

function sameSheet(a: string, b: string): boolean {
  return pathBase(a) === pathBase(b) || a.endsWith(b) || b.endsWith(a);
}

function isAgentFindingsJob(j: Job): boolean {
  return j.kind === "scene" && jobType(j.kind, j.kind_detail) === "agent_findings";
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

function changeHeading(id: string, title: string): string {
  const digits = id.match(/(\d+)(?!.*\d)/);
  return digits ? `#${digits[1]} ${title}` : title;
}

function statusPillClass(status: string): string {
  if (status === "approved" || status === "merged") return "border-[var(--copper)] text-[var(--copper)]";
  if (status === "open") return "border-emerald-600/70 text-emerald-400";
  return "border-border text-muted-foreground";
}

function checkDotClass(conclusion: string): string {
  if (conclusion === "passed") return "bg-emerald-500";
  if (conclusion === "failed" || conclusion === "timed_out") return "bg-falcon";
  return "bg-[#545457]";
}

export function FindingPin({
  label,
  onShow,
  showAction = false,
}: {
  label: string;
  onShow?: () => void;
  showAction?: boolean;
}) {
  return (
    <div className="inline-flex flex-wrap items-center gap-2" data-testid="finding-pin">
      <span className="inline-flex items-center rounded-full border border-[var(--copper)] bg-[color-mix(in_srgb,var(--copper)_16%,transparent)] px-2 py-0.5 font-mono text-[11px] text-[var(--copper)]">
        {label}
      </span>
      {showAction && onShow ? (
        <button
          type="button"
          className="text-[11px] text-[var(--copper)] underline-offset-2 hover:underline"
          data-testid="show-on-schematic"
          onClick={onShow}
        >
          Show on schematic
        </button>
      ) : null}
    </div>
  );
}

export function ReviewFileTree({
  items,
  selected,
  onSelect,
}: {
  items: { path: string; hint: string }[];
  selected: string | null;
  onSelect: (path: string) => void;
}) {
  return (
    <div className="min-h-0 overflow-auto border-r border-border bg-card p-2 font-mono text-xs" data-testid="review-file-tree">
      <div className="mb-2 px-1 text-[11px] tracking-wide text-muted-foreground uppercase">Files</div>
      {items.length === 0 ? (
        <p className="px-1 text-[11px] text-muted-foreground">No circuit-diff files yet.</p>
      ) : (
        items.map((f) => (
          <button
            key={f.path}
            type="button"
            className={cn(
              "mb-0.5 block w-full truncate rounded-none border-l-2 px-2 py-1.5 text-left",
              selected === f.path
                ? "border-[var(--copper)] bg-[#141414] text-foreground"
                : "border-transparent text-muted-foreground hover:bg-[#141414] hover:text-foreground",
            )}
            onClick={() => onSelect(f.path)}
          >
            <div className={f.hint.includes("remap") ? "text-falcon" : undefined}>{pathBase(f.path)}</div>
            <div className="truncate text-[10px] text-muted-foreground">{f.hint}</div>
          </button>
        ))
      )}
    </div>
  );
}

export function SchematicInset({
  svgSrc,
  pinLabel,
  empty,
}: {
  svgSrc: string | null;
  pinLabel: string | null;
  empty: string;
}) {
  return (
    <div
      className="relative min-h-[280px] overflow-auto rounded-[4px] border border-border"
      style={{ background: REVIEW_SCHEMATIC_INSET }}
      data-testid="schematic-inset"
    >
      {svgSrc ? (
        <img src={svgSrc} alt="candidate schematic" className="max-w-full" />
      ) : (
        <div className="flex h-[280px] items-center justify-center px-6 text-center font-mono text-xs text-[#545457]">
          {empty}
        </div>
      )}
      {pinLabel ? (
        <div className="absolute top-3 left-3">
          <FindingPin label={pinLabel} />
        </div>
      ) : null}
    </div>
  );
}

function GitHunks({ file, path }: { file?: CircuitDiffFile; path: string | null }) {
  if (!file && !path) {
    return (
      <div className="flex h-full min-h-[280px] items-center justify-center font-mono text-xs text-[#545457]">
        Select a file
      </div>
    );
  }
  if (file?.patch) {
    return <pre className="overflow-auto font-mono text-[11px] whitespace-pre-wrap text-[#0C0C0C]">{file.patch}</pre>;
  }
  return (
    <div className="font-mono text-xs text-[#545457]">
      <div>{path}</div>
      <div>
        {file?.status ?? "modified"} +{file?.additions ?? 0} −{file?.deletions ?? 0}
      </div>
      <div className="mt-2">No FileDiff.patch on this circuit_diff file.</div>
    </div>
  );
}

export function ReviewConversation({
  findings,
  findingsSummary,
  findingsJob,
  comments,
  comment,
  onCommentChange,
  onComment,
  onShowFinding,
}: {
  findings: AgentFinding[];
  findingsSummary: string | null;
  findingsJob: Job | null;
  comments: ReviewComment[];
  comment: string;
  onCommentChange: (value: string) => void;
  onComment: () => void;
  onShowFinding: (finding: AgentFinding) => void;
}) {
  const visible = findings.filter((f) => !(findingsSummary && f.code === "summary" && !f.path));
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="review-conversation" data-mission-id="panel:findings">
      <div className="min-h-0 flex-1 space-y-2 overflow-auto px-3 py-2 text-sm">
        {findingsSummary && <p className="text-sm font-medium">{findingsSummary}</p>}
        {visible.length === 0 && comments.length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            {findingsJob && (findingsJob.state === "queued" || findingsJob.state === "running")
              ? findingsWaitingCopy(findingsJob.state)
              : `No comments yet. Findings come from the agent_findings job on this review (R2), not the Think dock. ${AGENT_FINDINGS_NOT_A_GATE} Empty findings are not a pass. Think can walk a pin; it cannot Approve.`}
          </p>
        )}
        {visible.map((f, i) => {
          const pin = findingPinLabel(f);
          const tone =
            f.severity === "error" || f.severity === "warning"
              ? "border-falcon bg-falcon-muted"
              : "border-border";
          return (
            <div key={`${f.code ?? "f"}-${i}`} className={cn("rounded-[4px] border p-2", tone)}>
              <div className="mb-1 text-[10px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
                {f.severity || f.code || "finding"}
              </div>
              <div className="font-medium">{f.summary || f.code}</div>
              {f.path && <div className="font-mono text-[11px] text-muted-foreground">{f.path}</div>}
              {pin ? <div className="mt-2"><FindingPin label={pin} showAction onShow={() => onShowFinding(f)} /></div> : f.path ? (
                <button
                  type="button"
                  className="mt-2 text-[11px] text-[var(--copper)] underline-offset-2 hover:underline"
                  data-testid="show-on-schematic"
                  onClick={() => onShowFinding(f)}
                >
                  Show on schematic
                </button>
              ) : null}
            </div>
          );
        })}
        {comments.map((c) => (
          <div key={c.id} className="rounded-[4px] border border-border p-2 text-sm">
            <div className="mb-1 font-mono text-[10px] text-muted-foreground">{c.created_at}</div>
            {c.body}
          </div>
        ))}
      </div>
      <div className="shrink-0 space-y-2 border-t border-border p-3">
        <Textarea
          placeholder="Comment on path / pin…"
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
        />
        <Button size="sm" variant="outline" onClick={onComment}>
          Comment
        </Button>
      </div>
    </div>
  );
}

export function ReviewChecks({ jobs }: { jobs: Job[] }) {
  const runs = jobsCheckRuns(jobs);
  return (
    <div className="space-y-2 px-3 py-2 font-mono text-xs" data-testid="review-checks">
      <p className="text-[11px] text-muted-foreground">{COLLAB_IA.checksHint}</p>
      {runs.length === 0 ? (
        <p className="text-muted-foreground">No JobsChecks run on this change. Empty findings are not a pass.</p>
      ) : (
        <ul className="space-y-2">
          {runs.map((run) => {
            const conclusion = run.check_status || (run.state === "succeeded" ? "passed" : run.state);
            const label = jobTypeLabel(run.kind, run.kind_detail);
            return (
              <li key={run.id} className="flex items-start gap-2 rounded-[4px] border border-border bg-[#141414] p-2">
                <span className={cn("mt-1 size-2 shrink-0 rounded-full", checkDotClass(conclusion))} aria-hidden />
                <div className="min-w-0">
                  <div className="text-foreground">{label}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {run.id} · {checkConclusionLabel(conclusion === "succeeded" ? "passed" : conclusion)}
                  </div>
                  {label === "agent_findings" ? (
                    <div className="mt-1 text-[10px] text-muted-foreground">{AGENT_FINDINGS_NOT_A_GATE}</div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function ReviewStageLayout({
  files,
  center,
  side,
}: {
  files: ReactNode;
  center: ReactNode;
  side: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid min-h-0 min-w-0 flex-1 grid-cols-1 overflow-hidden rounded-[4px] border border-border",
        REVIEW_STAGE_COLS,
      )}
      data-testid="review-stage"
    >
      {files}
      <div className="min-w-0 overflow-auto bg-[#050505] p-3" data-testid="review-center">
        {center}
      </div>
      <div className="flex min-h-0 flex-col border-l border-border bg-card">{side}</div>
    </div>
  );
}

export function ReviewStage() {
  const { orgSlug, projectSlug, reviewId } = useParams();
  const { role } = useProjectScope();
  const [params] = useSearchParams();
  const [review, setReview] = useState<Review | null>(null);
  const [change, setChange] = useState<ChangeListItem | null>(null);
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [diff, setDiff] = useState<CircuitDiff | null>(null);
  const [findings, setFindings] = useState<AgentFinding[]>([]);
  const [findingsSummary, setFindingsSummary] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [revisions, setRevisions] = useState<HardwareRevision[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [centerTab, setCenterTab] = useState<"diff" | "schematic">("schematic");
  const [sideTab, setSideTab] = useState<"conversation" | "checks">("conversation");
  const [pinLabel, setPinLabel] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [deciding, setDeciding] = useState(false);
  const routeKey = `${orgSlug ?? ""}/${projectSlug ?? ""}/${reviewId ?? ""}`;
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const applyFindings = useCallback((env: FindingsEnvelope) => {
    setFindings(env.findings);
    setFindingsSummary(env.summary);
  }, []);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug || !reviewId) {
      finish(isCurrent);
      return;
    }
    try {
      const [body, hw, ch] = await Promise.all([
        getReview(orgSlug, projectSlug, reviewId),
        getHardware(orgSlug, projectSlug),
        getChange(orgSlug, projectSlug, reviewId).catch(() => null),
      ]);
      if (!isCurrent()) return;
      setReview(body.review);
      setChange(ch);
      setComments(body.comments ?? []);
      setJobs(body.jobs ?? []);
      setArtifacts(hw.artifacts ?? []);
      setRevisions(hw.revisions ?? []);
      const [cd, fd] = await Promise.all([
        getCircuitDiff(orgSlug, projectSlug, reviewId),
        getFindings(orgSlug, projectSlug, reviewId),
      ]);
      if (!isCurrent()) return;
      setDiff(cd);
      applyFindings(fd);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setErr(e instanceof Error ? e.message : "review load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, reviewId, applyFindings, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSelected(null);
    setPinLabel(null);
    setCenterTab("schematic");
  }, [reviewId]);

  const diffJob = jobs.find((j) => jobType(j.kind, j.kind_detail) === "circuit_diff") ?? null;
  const findingsJob = jobs.find(isAgentFindingsJob) ?? null;

  useEffect(() => {
    if (!orgSlug || !projectSlug || !diffJob) return;
    if (diffJob.state === "succeeded" || diffJob.state === "failed" || diffJob.state === "cancelled") return;
    const ac = new AbortController();
    void (async () => {
      try {
        await pollJob(orgSlug, projectSlug, diffJob.id, ac.signal);
        if (!ac.signal.aborted) await load();
      } catch {
        /* ignore abort */
      }
    })();
    return () => ac.abort();
  }, [diffJob?.id, diffJob?.state, orgSlug, projectSlug, load]);

  useEffect(() => {
    if (!orgSlug || !projectSlug || !reviewId) return;
    const inflight = findingsJob != null && (findingsJob.state === "queued" || findingsJob.state === "running");
    const succeededOrKeyed = findingsJob?.state === "succeeded" || Boolean(review?.agent_findings_r2_key);
    if (!inflight && !succeededOrKeyed) return;
    if (succeededOrKeyed && findings.length > 0) return;

    const ac = new AbortController();
    void (async () => {
      try {
        if (succeededOrKeyed) {
          const fd = await getFindings(orgSlug, projectSlug, reviewId);
          if (ac.signal.aborted) return;
          if (fd.findings.length > 0 || fd.summary) {
            applyFindings(fd);
            return;
          }
        }
        const attempts = inflight ? 3 : 2;
        for (let i = 0; i < attempts; i++) {
          await sleep(2000, ac.signal);
          if (ac.signal.aborted) return;
          const body = await getReview(orgSlug, projectSlug, reviewId);
          if (ac.signal.aborted) return;
          setReview(body.review);
          setComments(body.comments ?? []);
          setJobs(body.jobs ?? []);
          const fd = await getFindings(orgSlug, projectSlug, reviewId);
          if (ac.signal.aborted) return;
          applyFindings(fd);
          if (fd.findings.length > 0 || fd.summary) return;
          const next = (body.jobs ?? []).find(isAgentFindingsJob);
          if (next && (next.state === "failed" || next.state === "cancelled")) return;
        }
      } catch {
        /* ignore abort */
      }
    })();
    return () => ac.abort();
  }, [
    findingsJob?.id,
    findingsJob?.state,
    review?.agent_findings_r2_key,
    findings.length,
    orgSlug,
    projectSlug,
    reviewId,
    applyFindings,
  ]);

  const queryFrom = params.get("from_ref") || "";
  const queryTo = params.get("to_ref") || "";
  const oids = changeOidRange({
    baseline_oid: change?.baseline_oid,
    candidate_oid: change?.candidate_oid,
    baseline_snapshot: change?.baseline_snapshot,
    candidate_snapshot: change?.candidate_snapshot,
    from_oid: review?.from_oid,
    to_oid: review?.to_oid,
    from_ref: queryFrom || review?.from_ref,
    to_ref: queryTo || review?.to_ref,
  });
  const title = review?.title || change?.title || (oids.from && oids.to ? `${oids.from} → ${oids.to}` : "Design Review");
  const status = change?.review_status || review?.status || "open";

  const files: CircuitDiffFile[] = useMemo(() => {
    if (diff?.files?.length) return diff.files;
    const summary = parseJsonField<{ path: string; status?: string }[]>(review?.files_summary_json, []);
    return summary.map((f) => ({ path: f.path, status: f.status || "modified" }));
  }, [diff, review]);

  const highlights = diff?.highlights ?? [];
  const rail = useMemo(() => {
    const seen = new Set<string>();
    const out: { path: string; hint: string }[] = [];
    for (const h of highlights) {
      if (seen.has(h.path)) continue;
      seen.add(h.path);
      out.push({ path: h.path, hint: h.reason });
    }
    for (const f of files) {
      if (seen.has(f.path)) continue;
      seen.add(f.path);
      out.push({ path: f.path, hint: f.status });
    }
    return out;
  }, [highlights, files]);

  useEffect(() => {
    if (selected) return;
    setSelected(rail[0]?.path ?? null);
  }, [rail, selected]);

  const selectedFile = files.find((f) => selected && sameSheet(f.path, selected));
  const isSch = selected ? selected.toLowerCase().endsWith(".kicad_sch") : false;
  const isPcb = selected ? selected.toLowerCase().endsWith(".kicad_pcb") : false;
  const pairViews = (diff?.pair_views ?? []).filter((pv) => selected && sameSheet(pv.sheet_path, selected));
  const pinChanges = (diff?.pin_changes ?? []).filter((c) => selected && sameSheet(c.identity.sheet_path, selected));
  const toSvg = findImportSvg(artifacts, review?.to_oid || review?.to_ref || change?.candidate_oid || change?.candidate_snapshot);
  const svgSrc = orgSlug && projectSlug && toSvg ? artifactUrl(orgSlug, projectSlug, toSvg.id) : null;
  const toRev = revisions.find(
    (r) =>
      r.git_oid === review?.to_oid ||
      r.git_oid === change?.candidate_oid ||
      (!!review?.to_ref && (r.git_oid === review.to_ref || r.git_oid.startsWith(review.to_ref))),
  );
  const hasPcb = toRev ? revisionHasPcb(toRev) : true;
  const approveUi = reviewApproveUi(jobs, role);
  const canDecide = canDecideReview(role);

  function showFinding(finding: AgentFinding) {
    if (finding.path) setSelected(finding.path);
    setPinLabel(findingPinLabel(finding));
    setCenterTab("schematic");
  }

  async function onComment() {
    if (!orgSlug || !projectSlug || !reviewId || !comment.trim()) return;
    try {
      await postReviewComment(orgSlug, projectSlug, reviewId, {
        body: comment.trim(),
        anchor_kind: pinLabel ? "pin" : "general",
        anchor_json: selected
          ? { v1_kind: pinLabel ? "pin" : "file", path: selected, pin: pinLabel }
          : {},
      });
      setComment("");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "comment failed");
    }
  }

  async function onDecide(verdict: ReviewVerdict) {
    if (!orgSlug || !projectSlug || !reviewId) return;
    setDeciding(true);
    try {
      await decideReview(orgSlug, projectSlug, reviewId, verdict);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "decide failed");
    } finally {
      setDeciding(false);
    }
  }

  const schematicEmpty = !selected
    ? "Select a sheet. Collaboration does not invent a schematic."
    : isPcb && !hasPcb
      ? "Schematic-only revision — no `.kicad_pcb` in this zip"
      : "No import SVG for this candidate snapshot. Collaboration does not invent a board.";

  return (
    <AppShell
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
      title={
        loading ? (
          <div className="flex items-center gap-2" data-testid="review-title-skeleton">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-5 w-36" />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2" data-testid="review-decide-bar">
            <Badge variant="outline" className={statusPillClass(status)}>
              {reviewStatusLabel(status)}
            </Badge>
            <h1 className="min-w-0 truncate text-[16px] font-semibold tracking-[0.02em] text-white">
              {changeHeading(reviewId || change?.id || "", title)}
            </h1>
            {oids.label ? <Badge className="font-mono">{oids.label}</Badge> : null}
            <span className="sr-only">{COLLAB_IA.reviewKicker}</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {canDecide && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="review-request-changes"
                    disabled={deciding || status === "changes_requested"}
                    onClick={() => void onDecide("changes_requested")}
                  >
                    Request changes
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    data-testid="review-approve"
                    disabled={deciding || approveUi.disabled || status === "approved"}
                    title={approveUi.reason ?? undefined}
                    onClick={() => void onDecide("approved")}
                  >
                    Approve
                  </Button>
                </>
              )}
              <PresenceRail orgSlug={orgSlug} projectSlug={projectSlug} surface="review" objectId={reviewId} compact />
            </div>
          </div>
        )
      }
    >
      {err && <p className="border-b border-border px-3 py-2 text-sm text-destructive">{err}</p>}
      {approveUi.disabled && canDecide && status !== "approved" && (
        <p className="border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground" data-testid="review-gate-reason">
          {approveUi.reason}
        </p>
      )}
      {loading ? (
        <div className="min-h-0 flex-1 p-3">
          <ReviewStageSkeleton />
        </div>
      ) : (
        <ReviewStageLayout
          files={<ReviewFileTree items={rail} selected={selected} onSelect={setSelected} />}
          center={
            <div className="space-y-3">
              <div className="flex items-center gap-0 border-b border-border" role="tablist" aria-label="Center pane">
                {(
                  [
                    ["schematic", "Schematic"],
                    ["diff", "Diff"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={centerTab === id}
                    className={cn(
                      "px-3 py-1.5 text-[13px] font-medium",
                      centerTab === id
                        ? "border-b-2 border-[var(--copper)] text-foreground"
                        : "border-b-2 border-transparent text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setCenterTab(id)}
                  >
                    {label}
                  </button>
                ))}
                {diffJob && (
                  <span className="ml-auto font-mono text-[11px] text-muted-foreground">
                    {jobTypeLabel(diffJob.kind, diffJob.kind_detail)} · {diffJob.state}
                  </span>
                )}
              </div>
              {centerTab === "schematic" ? (
                <SchematicInset
                  svgSrc={isSch || !selected ? svgSrc : null}
                  pinLabel={pinLabel}
                  empty={schematicEmpty}
                />
              ) : isPcb && !hasPcb ? (
                <div className="flex h-full min-h-[280px] items-center justify-center font-mono text-xs text-muted-foreground">
                  Schematic-only revision — no `.kicad_pcb` in this zip
                </div>
              ) : (
                <div className="space-y-3 rounded-[4px] border border-border p-3" style={{ background: REVIEW_SCHEMATIC_INSET }}>
                  {!diff && diffJob && diffJob.state !== "succeeded" && (
                    <p className="font-mono text-xs text-falcon">circuit_diff {diffJob.state}</p>
                  )}
                  {pairViews.map((pv) => (
                    <div key={`${pv.sheet_path}-${pv.phy.refdes}`} className="overflow-auto rounded-md border border-border bg-white">
                      <div className="border-b border-border px-2 py-1 font-mono text-[11px] text-muted-foreground">
                        {pv.phy.refdes} ↔ {pv.magnetics.refdes} · {pv.sheet_path}
                      </div>
                      <table className="w-full text-left text-xs text-[#0C0C0C]">
                        <thead>
                          <tr className="text-[#545457]">
                            <th className="px-2 py-1">pair</th>
                            <th className="px-2 py-1">phy</th>
                            <th className="px-2 py-1">from mag</th>
                            <th className="px-2 py-1">to mag</th>
                            <th className="px-2 py-1">status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pv.pairs.map((row) => (
                            <tr key={row.id} className={row.status === "remap" ? "bg-falcon-muted" : undefined}>
                              <td className="px-2 py-1 font-mono">{row.id}</td>
                              <td className="px-2 py-1 font-mono">{row.phy_pins.join(",")}</td>
                              <td className="px-2 py-1 font-mono">{row.from_mag.join(",")}</td>
                              <td className="px-2 py-1 font-mono">{row.to_mag.join(",")}</td>
                              <td className="px-2 py-1">{row.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {pinChanges.length > 0 && (
                    <div className="rounded-md border border-border bg-white p-2 font-mono text-[11px] text-[#0C0C0C]">
                      {pinChanges.map((c, i) => {
                        const label = `${c.identity.refdes}.${c.to?.pin_name || c.from?.pin_name || c.identity.pin_number}`;
                        return (
                          <div key={`${c.identity.refdes}-${c.identity.pin_number}-${i}`} className="flex flex-wrap items-center gap-2 py-0.5">
                            <span>
                              {c.kind} {label} {c.from?.pin_name || ""} → {c.to?.pin_name || ""}{" "}
                              {c.from?.counterparts?.[0] ? `${c.from.counterparts[0].refdes}.${c.from.counterparts[0].pin_number}` : ""}
                              {c.to?.counterparts?.[0] ? ` → ${c.to.counterparts[0].refdes}.${c.to.counterparts[0].pin_number}` : ""}
                            </span>
                            <FindingPin
                              label={label}
                              showAction
                              onShow={() => {
                                setPinLabel(label);
                                setCenterTab("schematic");
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <GitHunks file={selectedFile} path={selected} />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">{COLLAB_IA.noMerge}</p>
            </div>
          }
          side={
            <>
              <div className="flex shrink-0 border-b border-border" role="tablist" aria-label="Review side">
                {(
                  [
                    ["conversation", "Conversation"],
                    ["checks", "Checks"],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={sideTab === id}
                    className={cn(
                      "px-3 py-2 text-[13px] font-medium",
                      sideTab === id
                        ? "border-b-2 border-[var(--copper)] text-foreground"
                        : "border-b-2 border-transparent text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => setSideTab(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {sideTab === "conversation" ? (
                <ReviewConversation
                  findings={findings}
                  findingsSummary={findingsSummary}
                  findingsJob={findingsJob}
                  comments={comments}
                  comment={comment}
                  onCommentChange={setComment}
                  onComment={() => void onComment()}
                  onShowFinding={showFinding}
                />
              ) : (
                <ReviewChecks jobs={jobs} />
              )}
            </>
          }
        />
      )}
    </AppShell>
  );
}
